"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import { extractProperty, extractPropertyMediaItems, getUmbracoMediaUrl } from "utils"
import PortContainer from "./PortContainer"
import PortPrimaryButton from "./PortPrimaryButton"
import PortTitle from "./PortTitle"

const StyledSection = styled.section<{ $darkMode?: boolean }>`
  --p-bg: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBackgroundColor
      : props.theme.colors.port.lightBackgroundColor};
  --p-text: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkTextColor
      : props.theme.colors.port.lightTextColor};
  --p-border-color: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.lightBorderColor};
  --p-border-color-hover: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.defaultTextColor};
  --p-item-bg: ${(props) => (props.$darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent")};
  --p-item-bg-hover: transparent;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  gap: 40px;
  background-color: var(--p-bg);
  color: var(--p-text);
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 120px 0;
  }
`

const StyledItemsWrapper = styled.ul`
  align-self: center;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 32px;
  }
`

const StyledItem = styled.li`
  margin: 0;
  padding: 0;
  overflow: hidden;
  color: var(--p-text);
  border: 1px solid var(--p-border-color);
  transition: all 0.3s ease-in-out;
  background-color: var(--p-item-bg);
  &:hover {
    border-color: var(--p-border-color-hover);
    background-color: var(--p-item-bg-hover);
  }

  display: flex;
  flex-direction: column;
  align-items: center;

  border-radius: 12px;
  width: 154px;
  gap: 8px;
  padding: 16px 12px;
  min-height: 212px;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    border-radius: 20px;
    min-height: 264px;
    width: 173px;
    gap: 16px;
    padding: 16px 12px 24px;
  }
`

const ItemText = styled.h4`
  margin: 0;
  padding: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%; /* 30px */
  letter-spacing: 0.6px;
  text-transform: capitalize;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px; /* 36px */
    letter-spacing: unset;
  }
`

const StyledItemIcon = styled(Image)`
  object-fit: contain;
  width: 100%;
  height: 100%;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
  }
`

type PortOurBenefitsProps = {
  data: any
}

const PortOurBenefits = ({ data }: PortOurBenefitsProps) => {
  const t = useTranslations("PortOurBenefits")
  const blackTitle = extractProperty(data, "blackTitle") || ""
  const blueTitle = extractProperty(data, "blueTitle") || ""
  const button = extractProperty(data, "button")?.[0] || null
  const darkMode = extractProperty(data, "darkMode") || false

  const itemsList = extractProperty(data, "itemsList") || []
  const items = itemsList?.map((item: any) => ({
    title: extractProperty(item, "title"),
    icon: extractPropertyMediaItems(item, "icon")?.[0],
    link: extractProperty(item, "link")?.[0],
  }))

  // in case data fields are empty, return null
  if (!blackTitle && !blueTitle && (!items || items.length === 0) && !button) {
    return null
  }
  return (
    <StyledSection id="port-our-benefits" $darkMode={!!darkMode}>
      {blackTitle && (
        <PortTitle
          normalText={blackTitle}
          highlightedText={blueTitle}
          centered
          overwrittenColor={darkMode ? "var(--p-text)" : undefined}
        />
      )}
      <PortContainer paddingInline={20}>
        {items && items.length > 0 && (
          <StyledItemsWrapper>
            {items?.map((item: any, index: number) => {
              const hasLink = item.link?.url && item.link?.name
              const content = (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: {
                        xs: "50px",
                        md: "60px",
                      },
                      height: {
                        xs: "50px",
                        md: "60px",
                      },
                    }}
                  >
                    {item.icon && (
                      <StyledItemIcon
                        src={getUmbracoMediaUrl(item.icon)}
                        alt={item.title || `icon-${index}`}
                        style={{ filter: darkMode ? "brightness(0) invert(1)" : "none" }}
                        width={60}
                        height={60}
                      />
                    )}
                  </Box>
                  {item.title && <ItemText>{item.title}</ItemText>}
                </>
              )

              return (
                <StyledItem key={index}>
                  {hasLink ? <StyledLink href={item.link.url}>{content}</StyledLink> : content}
                </StyledItem>
              )
            })}
          </StyledItemsWrapper>
        )}
      </PortContainer>
      <PortPrimaryButton
        sx={{
          fontSize: {
            xs: "18px",
          },
          minWidth: {
            xs: "calc(100% - 40px)",
            md: "200px",
          },
        }}
        textContent={button?.name || t("scheduleTour")}
        button={button}
      />
    </StyledSection>
  )
}
export default PortOurBenefits
